import { Controller } from "@hotwired/stimulus"

// Lays out and draws the tournament bracket.
//
// Replaces the jQuery block in application.js, which did three things: it
// staggered the columns so a match sits between the two it follows, it drew
// connector lines with LeaderLine, and it highlighted a team across the tree on
// hover. The line drawing never actually ran — it queried `#tree_pane`, an id
// that does not exist in any view — so the connectors are drawn here as a plain
// SVG overlay instead of pulling in a library.
//
// It also applies the parts of the tree that depend on who is looking. The
// server renders one tree for everybody so a result can be broadcast to every
// subscriber as a single fragment; the viewer's own rows are marked here.
export default class extends Controller {
    static targets = ["scroller", "column", "matches", "wrapper", "match", "teamRow", "teamBox", "lines", "state", "header"]
    static values = {
        myTeamIds: { type: Array, default: [] },
        mineLabel: { type: String, default: "" },
        finalRound: { type: Number, default: 0 },
        semiFinalRound: { type: Number, default: 0 },
        doubleElimination: { type: Boolean, default: false },
    }

    connect() {
        // Teams the reader has clicked to keep lit, so a mouseleave does not
        // take them down again.
        this.pinned = new Set()
        this.onResize = this.refresh.bind(this)
        this.onScroll = this.stickHeaders.bind(this)

        window.addEventListener("resize", this.onResize)
        // Capture: the page scrolls on one of several ancestors depending on the
        // layout, and scroll events do not bubble.
        document.addEventListener("scroll", this.onScroll, true)

        this.refresh()
    }

    disconnect() {
        window.removeEventListener("resize", this.onResize)
        document.removeEventListener("scroll", this.onScroll, true)
        cancelAnimationFrame(this.raf)
    }

    // --- sticky round headers ---------------------------------------------------
    //
    // Done by hand instead of position:sticky. #tree scrolls horizontally, which
    // makes it a scroll container on both axes, and a sticky child pins to its
    // nearest scroll container — so a CSS sticky header would pin to a box that
    // never scrolls vertically, and never move at all.
    //
    // Shifting the header inside its own column keeps it lined up horizontally
    // for free: it still scrolls sideways with the round it belongs to.
    stickHeaders() {
        if (!this.hasScrollerTarget || !this.hasHeaderTarget) return

        const tree = this.scrollerTarget.getBoundingClientRect()
        const height = this.headerTarget.offsetHeight
        // How far the top of the bracket has passed under the navbar…
        const passed = this.topInset() - tree.top
        // …but a header never leaves the column it labels.
        const offset = Math.max(0, Math.min(passed, tree.height - height))

        this.headerTargets.forEach((header) => {
            header.style.transform = offset ? `translateY(${offset}px)` : ""
        })
    }

    // The site header is sticky itself; the round headers stop below it.
    topInset() {
        const navbar = document.querySelector(".navbar")
        if (!navbar) return 0

        const rect = navbar.getBoundingClientRect()
        return rect.top <= 0 ? rect.height : Math.max(0, rect.bottom)
    }

    // A broadcast replaces the whole tree underneath this controller, so the
    // layout has to run again. One frame of debounce covers the burst of
    // connect callbacks a replacement fires.
    matchTargetConnected() {
        this.refresh()
    }

    refresh() {
        // Draw straight away so the tree is right even in a background tab,
        // where requestAnimationFrame does not fire, then once more after the
        // next frame: fonts and team logos land late and move the cells.
        this.draw()
        cancelAnimationFrame(this.raf)
        this.raf = requestAnimationFrame(() => this.draw())
    }

    draw() {
        this.layout()
        this.markOwnTeams()
        this.drawLines()
        this.stickHeaders()
        // drawLines() builds the paths from scratch; re-light whatever is pinned.
        this.pinned?.forEach((teamId) => this.toggleTeam(teamId, true))
    }

    // --- layout ---------------------------------------------------------------

    // A cell sits centred on the matches that feed it. That single rule gives
    // both shapes the bracket needs:
    //
    //   winner bracket   two feeders  -> centred between them
    //   loser bracket    one feeder   -> in line with it
    //
    // The loser bracket alternates: a whole round feeds the half round beside it
    // one-to-one (the losers dropping down from the winner bracket join there),
    // and the half round feeds the next whole round two-to-one. The old code
    // offset every column by 2^n regardless, which is only ever right for the
    // winner bracket.
    //
    // Feeders come from the same nextOf() the connector lines use, so the lines
    // and the layout cannot disagree.
    layout() {
        const cells = this.cells()
        if (cells.length === 0) return

        const byKey = new Map(cells.map((cell) => [this.keyFor(cell), cell]))
        const feeders = new Map()

        cells.forEach((cell) => {
            const next = this.successorKey(cell)
            if (!next || !byKey.has(next)) return

            const list = feeders.get(next) || []
            list.push(cell)
            feeders.set(next, list)
        })

        const pitch = cells[0].wrapper.offsetHeight
        if (!pitch) return

        // Columns left to right: a cell's feeders always sit in an earlier one,
        // so they are positioned by the time it is read.
        this.columnTargets.forEach((column) => {
            const wrappers = Array.from(column.querySelectorAll("[data-tournament-tree-target='wrapper']"))
            if (wrappers.length === 0) return

            let previous = null

            wrappers.forEach((wrapper, index) => {
                const cell = byKey.get(this.keyFor(this.cellFor(wrapper)))
                const sources = (cell && feeders.get(this.keyFor(cell))) || []
                const tops = sources.map((source) => source.top).filter((top) => top != null)

                // No feeders — the first round of a bracket, or a cell whose
                // predecessors have not been created yet. Stack it.
                const top = tops.length
                    ? (Math.min(...tops) + Math.max(...tops)) / 2
                    : (previous === null ? index * pitch : previous + pitch)

                wrapper.style.position = "absolute"
                wrapper.style.top = `${top}px`
                wrapper.style.left = "0"
                wrapper.style.right = "0"

                if (cell) cell.top = top
                previous = top
            })

            // Absolute children carry no height of their own.
            const container = wrappers[0].parentElement
            const deepest = wrappers.reduce((max, w) => Math.max(max, parseFloat(w.style.top) || 0), 0)
            container.style.position = "relative"
            container.style.height = `${deepest + pitch}px`
        })
    }

    cells() {
        return this.matchTargets.map((element) => this.cellFor(element.closest("[data-tournament-tree-target='wrapper']")))
                   .filter(Boolean)
    }

    cellFor(wrapper) {
        const element = wrapper?.querySelector("[data-tournament-tree-target='match']")
        if (!element) return null

        return {
            element,
            wrapper,
            bracket: element.dataset.matchBracket,
            round: parseFloat(element.dataset.matchRound),
            position: parseInt(element.dataset.matchPosition, 10),
            top: null,
        }
    }

    keyFor(cell) {
        return cell ? `${cell.bracket}|${cell.round}|${cell.position}` : null
    }

    // --- connectors -----------------------------------------------------------

    // A match feeds into exactly one follow-up: the winner bracket advances a
    // round and halves the position, the loser bracket advances half a round and
    // only halves the position on the half-rounds.
    nextOf(match) {
        const { bracket, round, position } = this.successor({
            bracket: match.dataset.matchBracket,
            round: parseFloat(match.dataset.matchRound),
            position: parseInt(match.dataset.matchPosition, 10),
        })
        return this.findMatch(bracket, round, position)
    }

    // Where a match's winner goes next.
    //
    //   winner bracket  next round, two matches collapsing into one
    //   loser bracket   whole round -> the half round beside it, same position;
    //                   half round  -> the next whole round, two into one
    successor({ bracket, round, position }) {
        const halved = (position % 2 ? position + 1 : position) / 2

        if (bracket === "winner") return { bracket, round: round + 1, position: halved }

        const wholeRound = round === Math.floor(round)
        return { bracket, round: round + 0.5, position: wholeRound ? position : halved }
    }

    successorKey(cell) {
        return this.keyFor({ ...this.successor(cell) })
    }

    findMatch(bracket, round, position) {
        return this.matchTargets.find((candidate) => (
            candidate.dataset.matchBracket === bracket &&
            parseFloat(candidate.dataset.matchRound) === round &&
            parseInt(candidate.dataset.matchPosition, 10) === position
        ))
    }

    // Where a match's loser goes, mirroring Tournament::Team's advancement:
    //
    //   double elimination  round 1        -> loser round 2, positions pairing up
    //                       round n (n>=2) -> loser round n.5, position mirrored
    //                                         within the round ((max + 1) - p)
    //   single elimination  the semi-final -> the third-place match
    //
    // Only drawn for the viewer's own team; a full set of these was too noisy.
    loserTarget(cell) {
        if (cell.bracket !== "winner") return null

        const halved = (cell.position % 2 ? cell.position + 1 : cell.position) / 2

        if (this.doubleEliminationValue) {
            if (cell.round === 1) return { bracket: "loser", round: 2, position: halved }

            const mirrored = this.roundWidth("winner", cell.round) + 1 - cell.position
            return { bracket: "loser", round: cell.round + 0.5, position: mirrored }
        }

        if (this.finalRoundValue && cell.round === this.finalRoundValue - 1) {
            return { bracket: "loser", round: this.finalRoundValue, position: halved }
        }
        return null
    }

    // How many matches a round holds — the mirror above counts from it. Taken
    // from the DOM, which renders one cell per position whether the match
    // exists yet or not.
    roundWidth(bracket, round) {
        return this.matchTargets.filter((match) => (
            match.dataset.matchBracket === bracket &&
            parseFloat(match.dataset.matchRound) === round
        )).length
    }

    // The one climb back out of the loser bracket: its final feeds the grand final.
    promotionTarget(cell) {
        if (!this.doubleEliminationValue || cell.bracket !== "loser") return null
        if (cell.round !== this.semiFinalRoundValue) return null

        const halved = (cell.position % 2 ? cell.position + 1 : cell.position) / 2
        return { bracket: "winner", round: this.finalRoundValue, position: halved }
    }

    drawLines() {
        if (!this.hasLinesTarget || !this.hasScrollerTarget) return

        const scroller = this.scrollerTarget
        const svg = this.linesTarget
        const origin = scroller.getBoundingClientRect()
        const width = scroller.scrollWidth
        const height = scroller.scrollHeight

        svg.setAttribute("viewBox", `0 0 ${width} ${height}`)
        svg.setAttribute("width", width)
        svg.setAttribute("height", height)
        svg.replaceChildren()

        const segments = []

        this.matchTargets.forEach((match) => {
            const cell = {
                bracket: match.dataset.matchBracket,
                round: parseFloat(match.dataset.matchRound),
                position: parseInt(match.dataset.matchPosition, 10),
            }

            // Who came out of this match, and who dropped. Both only known once
            // it has been decided — an undecided match leads nowhere yet, and the
            // `me` marks come from markOwnTeams(), which has already run.
            const rows = Array.from(match.querySelectorAll("[data-tournament-tree-target='teamRow']"))
            const winner = rows.find((row) => row.classList.contains("winner"))
            const loser = winner ? rows.find((row) => row !== winner) : null

            const decided = match.querySelector("[data-tournament-tree-target='state']")
                                 ?.dataset.state === "finished"
            const onwards = winner?.classList.contains("me") ? "mine" : (decided ? "done" : null)

            const advance = { from: match, variant: onwards, teamId: winner?.dataset.teamId }
            segments.push({ ...advance, to: this.nextOf(match) })
            segments.push({ ...advance, to: this.resolve(this.promotionTarget(cell)) })

            // The way down is drawn for everyone but kept hidden: it only shows
            // for your own team, or for the team the cursor is on.
            if (loser) {
                segments.push({
                    from: match,
                    to: this.resolve(this.loserTarget(cell)),
                    variant: loser.classList.contains("me") ? "mine" : null,
                    teamId: loser.dataset.teamId,
                    drop: true,
                })
            }
        })

        // Your own path is drawn last so it sits on top of anything it crosses.
        segments.sort((a, b) => (a.variant === "mine" ? 1 : 0) - (b.variant === "mine" ? 1 : 0))
        segments.forEach(({ from, to, variant, teamId, drop }) => {
            if (!to) return

            const path = this.connector(this.anchor(from, origin, scroller, "right"),
                                        this.anchor(to, origin, scroller, "left"),
                                        variant, drop)
            // Lets a hover light up everything one team travelled along.
            if (teamId && teamId !== "0") path.dataset.teamId = teamId
            svg.appendChild(path)
        })
    }

    resolve(target) {
        return target ? this.findMatch(target.bracket, target.round, target.position) : null
    }

    // Point on the edge of a cell, in the coordinate space of the scrolled tree.
    anchor(element, origin, scroller, side) {
        const box = element.getBoundingClientRect()
        return {
            x: (side === "right" ? box.right : box.left) - origin.left + scroller.scrollLeft,
            y: box.top + box.height / 2 - origin.top + scroller.scrollTop,
        }
    }

    // Two right angles: out of the source, across, into the target.
    connector(from, to, variant, drop) {
        const middle = from.x + (to.x - from.x) / 2
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path")

        path.setAttribute("d", `M ${from.x} ${from.y} H ${middle} V ${to.y} H ${to.x}`)
        path.setAttribute("class", [
            "tree__line",
            drop && "tree__line--drop",
            variant && `tree__line--${variant}`,
        ].filter(Boolean).join(" "))
        return path
    }

    // --- the viewer -----------------------------------------------------------

    markOwnTeams() {
        if (this.myTeamIdsValue.length === 0) return

        const mine = this.myTeamIdsValue.map(String)

        this.teamRowTargets.concat(this.teamBoxTargets).forEach((element) => {
            if (mine.includes(element.dataset.teamId)) element.classList.add("me")
        })

        this.matchTargets.forEach((match) => {
            const rows = Array.from(match.querySelectorAll("[data-tournament-tree-target='teamRow']"))
            const state = match.querySelector("[data-tournament-tree-target='state']")
            if (!state || state.dataset.state === "finished") return
            if (!rows.some((row) => mine.includes(row.dataset.teamId))) return

            state.dataset.state = "mine"
            const icon = state.querySelector("i")
            if (!icon) return

            icon.classList.remove("fa-play", "fa-triangle-exclamation")
            icon.classList.add("fa-crosshairs")
            icon.setAttribute("title", this.mineLabelValue)
        })
    }

    // --- hover ----------------------------------------------------------------
    //
    // Wired through data-action on the elements themselves rather than a jQuery
    // delegate, so it survives a re-render.

    highlight(event) {
        this.toggleTeam(this.teamIdFrom(event), true)
    }

    unhighlight(event) {
        const teamId = this.teamIdFrom(event)
        if (this.pinned.has(teamId)) return

        this.toggleTeam(teamId, false)
    }

    // Clicking a team in the list keeps it lit until it is clicked again.
    pin(event) {
        event.preventDefault()
        // Bootstrap closes the menu on any click inside it; picking a team is
        // exactly when you want to stay and pick another.
        event.stopPropagation()

        const teamId = this.teamIdFrom(event)
        if (!teamId) return

        const on = !this.pinned.has(teamId)
        on ? this.pinned.add(teamId) : this.pinned.delete(teamId)

        this.toggleTeam(teamId, on)
        event.currentTarget.classList.toggle("is-pinned", on)
    }

    teamIdFrom(event) {
        return event.currentTarget.closest("[data-team-id]")?.dataset.teamId
    }

    toggleTeam(teamId, on) {
        if (!teamId || teamId === "0") return

        this.teamRowTargets.concat(this.teamBoxTargets).forEach((element) => {
            if (element.dataset.teamId === teamId) element.classList.toggle("selected", on)
        })

        // The same run through the bracket the rows show, drawn along the lines —
        // including the drop into the loser bracket, which is hidden otherwise.
        this.linesFor(teamId).forEach((path) => {
            path.classList.toggle("tree__line--selected", on)
            if (on) path.parentNode.appendChild(path)
        })
    }

    linesFor(teamId) {
        if (!this.hasLinesTarget) return []

        return Array.from(this.linesTarget.querySelectorAll(`[data-team-id="${teamId}"]`))
    }
}
